import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { useAuth } from '@/auth/useAuth';
import {
  LEAD_CATEGORIES,
  LEAD_SOURCES,
  LEAD_STATUSES,
  uid,
  type Lead,
} from './leadDomain';

interface Props {
  open: boolean;
  editing: Lead | null;
  people: string[];
  onClose: () => void;
  onSave: (lead: Lead) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

function blank(people: string[], defaultAssignee = ''): Lead {
  return {
    id: '', date: today(), customer: '', contactNumber: '', email: '',
    category: LEAD_CATEGORIES[0], source: LEAD_SOURCES[0],
    // Default a new lead to the logged-in user (falls back to the first team
    // member, then unassigned).
    referralName: '', assignedTo: defaultAssignee || people[0] || '', samplingCostInr: 0, status: LEAD_STATUSES[0],
    valueInr: 0, notes: '', comms: [],
  };
}

export function LeadFormModal({ open, editing, people, onClose, onSave }: Props) {
  const { user, hasPerm } = useAuth();
  // Only users granted the 'lead-assign' permission (see Role Management) can
  // assign/reassign a lead to another team member; everyone else stays locked
  // to themselves. `people` is the lead team (sales staff), so the SUPER_ADMIN
  // account is never an option — assignments show under a real team member.
  const canAssign = hasPerm('lead-assign');
  const [f, setF] = useState<Lead>(blank(people));
  const [err, setErr] = useState(false);
  // Keep an existing assignee visible even if they're not (or no longer) on the
  // team, so a prior assignment isn't silently dropped from the dropdown.
  const assigneeOptions = !f.assignedTo || people.includes(f.assignedTo) ? people : [f.assignedTo, ...people];

  useEffect(() => {
    if (!open) return;
    const me = user?.fullName ?? '';
    // Default assignee: managers/lead-access users default to themselves only
    // when they're on the team (so SUPER_ADMIN & non-team users start
    // Unassigned and must pick); everyone else is locked to themselves.
    const def = canAssign ? (people.includes(me) ? me : '') : me;
    if (editing) {
      setF({ ...editing, assignedTo: editing.assignedTo?.trim() ? editing.assignedTo : def });
    } else {
      setF({ ...blank(people, me), assignedTo: def });
    }
    setErr(false);
  }, [open, editing, people, user, canAssign]);

  function save() {
    if (!f.customer.trim()) { setErr(true); return; }
    onSave({
      ...f,
      id: f.id || uid(),
      customer: f.customer.trim(),
      contactNumber: f.contactNumber.trim(),
      email: f.email.trim(),
      referralName: f.source === 'Referral' ? f.referralName.trim() : '',
      samplingCostInr: Number(f.samplingCostInr) || 0,
      valueInr: Number(f.valueInr) || 0,
    });
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit lead' : 'Log a new lead'}
      size="md"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save}>Save lead</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="Date">
          <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </FormGroup>
        <FormGroup label="Category">
          <Select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value as Lead['category'] })}>
            {LEAD_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </FormGroup>

        <FormGroup label="Customer / inquirer name" required error={err ? 'Customer name is required' : null} className="col-span-2">
          <Input
            invalid={err}
            value={f.customer}
            onChange={(e) => { setF({ ...f, customer: e.target.value }); setErr(false); }}
            placeholder="e.g. Mr. Sharma residence, Studio Arcline Architects"
          />
        </FormGroup>

        <FormGroup label="Phone / WhatsApp">
          <Input
            type="tel"
            inputMode="tel"
            value={f.contactNumber}
            onChange={(e) => setF({ ...f, contactNumber: e.target.value })}
            placeholder="e.g. 98765 43210"
          />
        </FormGroup>
        <FormGroup label="Email">
          <Input
            type="email"
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            placeholder="e.g. name@company.com"
          />
        </FormGroup>

        <FormGroup label="Source">
          <Select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value as Lead['source'] })}>
            {LEAD_SOURCES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </FormGroup>
        {f.source === 'Referral' ? (
          <FormGroup label="Referral name">
            <Input value={f.referralName} onChange={(e) => setF({ ...f, referralName: e.target.value })} placeholder="Who referred this?" />
          </FormGroup>
        ) : <div />}

        {/* Users with lead access can assign to any team member; everyone else
            is locked to themselves (auto-assigned). */}
        <FormGroup label="Assigned to" hint={canAssign ? 'Assign this lead to a team member.' : "Set to you automatically; can't be changed once saved."}>
          {canAssign ? (
            <Select value={f.assignedTo} onChange={(e) => setF({ ...f, assignedTo: e.target.value })}>
              <option value="">— Unassigned —</option>
              {assigneeOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          ) : (
            <Input value={f.assignedTo || '— Unassigned —'} readOnly disabled />
          )}
        </FormGroup>
        <FormGroup label="Status">
          <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as Lead['status'] })}>
            {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </FormGroup>

        <FormGroup label="Sampling cost (₹, if any)">
          <Input type="number" value={f.samplingCostInr || ''} onChange={(e) => setF({ ...f, samplingCostInr: Number(e.target.value) })} placeholder="0" />
        </FormGroup>
        <FormGroup label="Order value if converted (₹)">
          <Input type="number" value={f.valueInr || ''} onChange={(e) => setF({ ...f, valueInr: Number(e.target.value) })} placeholder="0" />
        </FormGroup>

        <FormGroup label="Notes" className="col-span-2">
          <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </FormGroup>
      </div>
    </Modal>
  );
}
